import type { UseFormReturn } from 'react-hook-form';
import { Badge } from '../../../components/ui/Badge';
import { Section } from '../../../components/ui/Section';
import { asRecord, str } from '../../../platform/api/utils';
import {
  type PersonForm as EditForm,
  STATUS_LABELS,
  STATUS_VARIANTS,
  MEMBERSHIP_LABELS,
  Field,
  fmtDate,
  dlGrid,
  Dt,
  Dd,
  inputCss,
  primaryBtn,
  secondaryBtn,
} from './pessoa-detail.support';
import { PessoaAccessSection } from './pessoa-access.section';
import { usePermissions } from '../../../platform/auth/permissions';

export function PessoaDadosTab(props: {
  editing: boolean;
  form: UseFormReturn<EditForm>;
  p: Record<string, unknown>;
  updatePending: boolean;
  onCancelEdit: () => void;
  onSave: (data: EditForm) => void;
}) {
  const { editing, form, p, updatePending, onCancelEdit, onSave } = props;
  const { can } = usePermissions();
  return (
    <>
      {editing ? (
        <Section title="Editar dados">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem 1.5rem' }}>
            <Field label="Nome civil" error={form.formState.errors.civilName?.message}>
              <input {...form.register('civilName')} style={inputCss} />
            </Field>
            <Field label="Nome social">
              <input {...form.register('socialName')} style={inputCss} />
            </Field>
            <Field label="Nome religioso">
              <input {...form.register('religiousName')} style={inputCss} />
            </Field>
            {can('people.oriki.update') && (
              <Field label="Oríkì (opcional, sensível)">
                <textarea
                  {...form.register('oriki')}
                  style={{ ...inputCss, minHeight: 64, resize: 'vertical' }}
                  placeholder="Campo sensível — não obrigatório"
                />
              </Field>
            )}
            <Field label="Data nascimento">
              <input {...form.register('birthDate')} type="date" style={inputCss} />
            </Field>
            <Field label="E-mail" error={form.formState.errors.email?.message}>
              <input {...form.register('email')} type="email" style={inputCss} />
            </Field>
            <Field label="Telefone">
              <input {...form.register('phone')} style={inputCss} />
            </Field>
          </div>
          <div
            style={{
              display: 'flex',
              gap: '0.5rem',
              marginTop: '1rem',
              justifyContent: 'flex-end',
            }}
          >
            <button type="button" onClick={onCancelEdit} style={secondaryBtn}>
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => {
                void form.handleSubmit(onSave)();
              }}
              disabled={updatePending}
              style={primaryBtn}
            >
              {updatePending ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </Section>
      ) : (
        <>
          <Section title="Identidade">
            <dl style={dlGrid}>
              <Dt>Nome civil</Dt>
              <Dd>{str(p.civilName, '—')}</Dd>
              <Dt>Nome social</Dt>
              <Dd>{str(p.socialName, '—')}</Dd>
              <Dt>Nome religioso</Dt>
              <Dd style={{ fontStyle: str(p.religiousName) ? 'italic' : 'normal' }}>
                {str(p.religiousName, '—')}
              </Dd>
              {can('people.oriki.read') && (
                <>
                  <Dt>Oríkì</Dt>
                  <Dd>
                    {str(p.oriki) ? (
                      <span style={{ whiteSpace: 'pre-wrap' }}>{str(p.oriki)}</span>
                    ) : (
                      <span style={{ color: 'var(--faint)' }}>Não informado (opcional)</span>
                    )}
                  </Dd>
                </>
              )}
            </dl>
            <p style={{ fontSize: '0.75rem', color: 'var(--muted)', margin: '0.75rem 0 0' }}>
              Oríkì é campo sensível e opcional. Não é usado para permissão, ranking ou comunicação
              automática.
            </p>
          </Section>
          <Section title="Contato">
            <dl style={dlGrid}>
              <Dt>E-mail</Dt>
              <Dd>{str(p.email, '—')}</Dd>
              <Dt>Telefone</Dt>
              <Dd>{str(p.phone, '—')}</Dd>
              <Dt>Nascimento</Dt>
              <Dd>{fmtDate(p.birthDate)}</Dd>
            </dl>
          </Section>
          <PessoaAccessSection p={p} />
        </>
      )}
    </>
  );
}

export function PessoaCargosTab(props: { functionAssignments: Record<string, unknown>[] }) {
  const { functionAssignments } = props;
  return (
    <Section title="Cargos e funções da casa">
      <p style={{ fontSize: '0.8rem', color: 'var(--muted)', margin: '0 0 0.75rem' }}>
        Cargos institucionais não liberam acesso digital. Gerencie em “Cargos e funções”.
      </p>
      {functionAssignments.length === 0 ? (
        <p style={{ color: 'var(--faint)', fontSize: '0.875rem' }}>Nenhum cargo ativo vinculado.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {functionAssignments.map((raw) => {
            const a = asRecord(raw);
            const fn = asRecord(a.function);
            return (
              <div
                key={str(a.id)}
                style={{
                  padding: '0.5rem 0.75rem',
                  background: 'var(--paper)',
                  borderRadius: 10,
                  border: '1px solid var(--line-soft)',
                }}
              >
                <span style={{ fontWeight: 500 }}>{str(fn.name)}</span>
                <span style={{ marginLeft: 8, fontSize: '0.75rem', color: 'var(--muted)' }}>
                  {str(fn.type)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
}

export function PessoaVinculoTab(props: {
  currentMembership: Record<string, unknown> | null | undefined;
}) {
  const { currentMembership } = props;
  return (
    <Section title="Vínculo com a casa">
      {currentMembership ? (
        <dl style={dlGrid}>
          <Dt>Tipo de vínculo</Dt>
          <Dd>{MEMBERSHIP_LABELS[str(currentMembership.type)] ?? str(currentMembership.type)}</Dd>
          <Dt>Status do vínculo</Dt>
          <Dd>
            <Badge label={str(currentMembership.status, 'Ativo')} variant="success" />
          </Dd>
          <Dt>Início do vínculo</Dt>
          <Dd>{fmtDate(currentMembership.startedAt)}</Dd>
        </dl>
      ) : (
        <p style={{ color: 'var(--faint)', fontSize: '0.875rem' }}>
          Nenhum vínculo formal registrado.
        </p>
      )}
      <p
        style={{
          fontSize: '0.75rem',
          color: 'var(--muted)',
          marginTop: '1rem',
          padding: '0.5rem 0.75rem',
          background: 'var(--paper)',
          borderRadius: 10,
          border: '1px solid var(--line-soft)',
        }}
      >
        Dados de vínculo com a casa são sensíveis. Cadastre apenas o necessário para a organização
        administrativa.
      </p>
    </Section>
  );
}

export function PessoaHistoricoTab(props: { history: Record<string, unknown>[] }) {
  const { history } = props;
  return (
    <Section title="Histórico de status">
      {history.length === 0 ? (
        <p style={{ color: 'var(--faint)', fontSize: '0.875rem' }}>
          Nenhum registro de alteração de status.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {history.map((h, i) => {
            const rec = asRecord(h);
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.5rem 0.75rem',
                  background: 'var(--paper)',
                  borderRadius: 10,
                  border: '1px solid var(--line-soft)',
                  fontSize: '0.8rem',
                }}
              >
                <span style={{ color: 'var(--faint)', minWidth: 90 }}>
                  {fmtDate(rec.createdAt)}
                </span>
                <Badge
                  label={STATUS_LABELS[str(rec.oldStatus)] ?? str(rec.oldStatus ?? '—')}
                  variant={STATUS_VARIANTS[str(rec.oldStatus)] ?? 'default'}
                />
                <span style={{ color: 'var(--line)' }}>→</span>
                <Badge
                  label={STATUS_LABELS[str(rec.newStatus)] ?? str(rec.newStatus)}
                  variant={STATUS_VARIANTS[str(rec.newStatus)] ?? 'default'}
                />
                {!!rec.reason && (
                  <span style={{ color: 'var(--muted)', marginLeft: 'auto', fontStyle: 'italic' }}>
                    {str(rec.reason)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
}
